import React, { useEffect, useMemo, useState } from 'react';
import FeeCompareTable from './FeeCompareTable';
import { projectFees, sumFees, fuzzyFindCurrentSchool, formatRM } from '../../lib/reportUtils';

/**
 * The fee centrepiece for the #1 school.
 * Case A: current school is in our 122-DB → side-by-side comparison.
 * Case B: current school NOT in DB → parent enters current annual fee, live table.
 * Case C: recommended school has no fee data → contact-directly fallback.
 */
const FeeSection = ({
  schoolsList,            // array of {id, name, full_name} for lookup
  recommendedSchool,      // full school object from top_schools[0]
  currentSchoolName,      // string from questionnaire
  currentGrade,           // number 1..13
  currentFeeFromReport,   // fee pre-computed by backend (Case A fast path)
}) => {
  const [inputFee, setInputFee] = useState('');
  const [currency, setCurrency] = useState('RM');

  // Clear input when external inputs change (new report / different child)
  useEffect(() => { setInputFee(''); }, [recommendedSchool?.id, currentSchoolName]);

  const recAnnualFees = recommendedSchool?.annual_fees || {};
  const recHasFees = Object.values(recAnnualFees).some((v) => typeof v === 'number' && v > 0);

  const startGrade = currentGrade || 1;
  const recRows = useMemo(
    () => projectFees(recAnnualFees, startGrade, 11),
    [recAnnualFees, startGrade]
  );
  const sumRec = sumFees(recRows);

  // Try Case A — find current school in our list
  const currentSchoolMatch = useMemo(
    () => fuzzyFindCurrentSchool(currentSchoolName, schoolsList || []),
    [currentSchoolName, schoolsList]
  );

  // Case C — recommended school has no fees at all
  if (!recHasFees) {
    return (
      <div className="rounded-2xl bg-[#FFF5EB] border border-[#E8DED3] p-6" data-testid="fee-section-unavailable">
        <h4 className="text-lg font-bold text-[#2C2823] mb-2">Fees not publicly available</h4>
        <p className="text-[#6B6560] mb-4">
          {recommendedSchool?.fees_unavailable_message ||
            "This school doesn't publish year-by-year fees. Contact the admissions team directly for their current fee schedule."}
        </p>
        <a
          href={recommendedSchool?.admissions_url || '#'}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#E85D35] hover:text-[#C84B31]"
        >
          Contact admissions →
        </a>
      </div>
    );
  }

  // Case A — current school in our DB → proper side-by-side
  if (currentSchoolMatch && currentSchoolMatch.id) {
    // Fetch current school fees from the schoolsList if they're embedded; otherwise use currentFeeFromReport
    // Our /schools/list endpoint doesn't return fees, so fall back to a single-year projection from currentFeeFromReport.
    const currentAnnualFees = currentSchoolMatch.annual_fees || null;
    const currentRows = currentAnnualFees
      ? projectFees(currentAnnualFees, startGrade, 11)
      : (currentFeeFromReport
          ? projectFees({ [String(startGrade)]: currentFeeFromReport }, startGrade, 11)
          : []);
    const sumCurr = sumFees(currentRows);

    return (
      <div data-testid="fee-section-caseA">
        <FeeCompareTable
          currentRows={currentRows}
          recommendedRows={recRows}
          sumCurrent={sumCurr}
          sumRecommended={sumRec}
          currentLabel={currentSchoolMatch.name || currentSchoolMatch.full_name || 'Your current school'}
          recommendedLabel={recommendedSchool?.common_name || recommendedSchool?.full_name || 'Recommended'}
        />
      </div>
    );
  }

  // Case B — current school not in DB: show rec fees + parent enters their fee
  const inputNum = parseFloat(inputFee);
  const currentFeeRM = !isNaN(inputNum) && inputNum > 0
    ? (currency === 'USD' ? inputNum * 4.7 : inputNum) // rough USD->RM
    : null;
  const currentRows = currentFeeRM
    ? projectFees({ [String(startGrade)]: currentFeeRM }, startGrade, 11)
    : [];
  const sumCurr = sumFees(currentRows);

  return (
    <div data-testid="fee-section-caseB">
      {!currentFeeRM ? (
        <div className="mb-4">
          <h4 className="text-lg font-bold text-[#2C2823] mb-3">
            {recommendedSchool?.common_name || 'Recommended school'} fees
          </h4>
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#6B6560]">
                  <th className="py-2 pr-4 font-semibold">Year</th>
                  <th className="py-2 font-semibold">Annual fee</th>
                </tr>
              </thead>
              <tbody>
                {recRows.map((r) => (
                  <tr key={r.year} className="border-t border-[#E8DED3]">
                    <td className="py-2.5 pr-4 font-semibold text-[#2C2823]">Y{r.year}</td>
                    <td className="py-2.5 text-[#2C2823]">
                      {formatRM(r.fee)}
                      {r.projected && r.fee != null && (
                        <span className="ml-1 text-xs text-[#9B8F88]">proj</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[#6B6560] mt-1">
            Total projected: <span className="font-semibold text-[#2C2823]">{formatRM(sumRec)}</span>
          </p>
        </div>
      ) : null}

      <div className="rounded-xl bg-[#FFF5EB] border border-[#E8DED3] p-5">
        <label className="block text-sm font-semibold text-[#2C2823] mb-2" htmlFor="current-fee-input">
          What's your current annual fee?
        </label>
        <div className="flex gap-3 items-center">
          <div className="inline-flex rounded-lg border border-[#E8DED3] bg-white overflow-hidden">
            {['RM', 'USD'].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCurrency(c)}
                data-testid={`currency-${c}`}
                className={`px-3 py-2 text-sm font-semibold ${
                  currency === c ? 'bg-[#E85D35] text-white' : 'text-[#6B6560] hover:text-[#2C2823]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <input
            id="current-fee-input"
            data-testid="current-fee-input"
            type="number"
            min="0"
            inputMode="numeric"
            value={inputFee}
            onChange={(e) => setInputFee(e.target.value)}
            placeholder={currency === 'USD' ? 'e.g. 20000' : 'e.g. 85000'}
            className="flex-1 px-3 py-2 rounded-lg border border-[#E8DED3] bg-white focus:outline-none focus:border-[#E85D35]"
          />
        </div>
        <p className="text-xs text-[#9B8F88] mt-2">
          Entered for this session only — we don't save this to your profile.
        </p>
      </div>

      {currentFeeRM && (
        <div className="mt-5">
          <FeeCompareTable
            currentRows={currentRows}
            recommendedRows={recRows}
            sumCurrent={sumCurr}
            sumRecommended={sumRec}
            currentLabel="Your current school"
            recommendedLabel={recommendedSchool?.common_name || 'Recommended'}
          />
        </div>
      )}
    </div>
  );
};

export default FeeSection;
